// Schemas for routes-voice.ts (browser-safe zod). Aircall voice channel (PRD §11, §8.2, §13, §19).
//
// Honesty rules encoded here:
//  • every Aircall capability carries a state (verified / unverified / unsupported / failed); nothing is enabled
//    because the schema allows it;
//  • a provider 202/201 is `provider_accepted`, never "answered";
//  • analysis (transcript/summary) has its own status and is never fabricated;
//  • a voice prompt deployment is `action_required` until an admin confirms it was applied in Aircall (A22).
import { z } from 'zod';
import { CallControlKindSchema, CallControlsSummarySchema, CallEndReasonSchema } from './api-call-controls.js';
import { CapabilityReportSchema } from './api.js';
import { CallConsentSchema, CallRecordingStateSchema } from './api-recording.js';
import { AGENT_TOOLS, AnalysisStatusSchema, CallStatusSchema, DeploymentStatusSchema, DirectionSchema } from './enums.js';
import { VoiceLanguageChangedFields, VoiceSessionVoiceSchema } from './api-voice-persona.js';
import { LiveTransferViewSchema } from './api-priorities.js';

const Iso = z.string();
const Id = z.string().min(1).max(64);

/** Capability keys of an Aircall connection (see libs/backend/src/voice/capabilities.ts for their meaning). */
export const VOICE_CAPABILITIES = [
  'account',
  'lines',
  'outbound_ai_calls',
  'queue_expiry',
  'idempotent_replay',
  'call_lookup',
  'webhooks',
  'live_actions',
  'session_binding',
  'transcripts',
  'recordings',
  'inbound_calls',
  'agent_configuration',
  'inbound_routing',
  'voice_service',
  'telephony_leg',
] as const;
export const VoiceCapabilitySchema = z.enum(VOICE_CAPABILITIES);
export type VoiceCapability = z.infer<typeof VoiceCapabilitySchema>;

export const VOICE_CAPABILITY_LABELS: Record<VoiceCapability, string> = {
  account: 'Cuenta y credencial',
  lines: 'Líneas visibles',
  outbound_ai_calls: 'Llamadas salientes con agente de IA',
  queue_expiry: 'Vencimiento de la cola del proveedor',
  idempotent_replay: 'Reintento idempotente (misma clave)',
  call_lookup: 'Consulta de llamadas para conciliar',
  webhooks: 'Eventos de Aircall (webhooks)',
  live_actions: 'Acciones en vivo del agente',
  session_binding: 'Vinculación de sesión (proyecto/contacto)',
  transcripts: 'Transcripción y resumen',
  recordings: 'Grabaciones',
  inbound_calls: 'Llamadas entrantes',
  agent_configuration: 'Configuración del agente de voz',
  inbound_routing: 'Ruteo de entrantes (línea → agente de IA / ruta sin IA)',
  voice_service: 'Servicio de voz propio (sesión real completada)',
  telephony_leg: 'Tramo telefónico (desvío de Aircall al servicio de voz)',
};

/** Live actions the Aircall AI agent may call (bootstrap + the shared agent tools used by voice). */
export const VOICE_LIVE_ACTIONS = [
  'bootstrap_context',
  'get_project_context',
  'search_knowledge',
  'list_matching_units',
  'get_unit',
  'get_current_price',
  'get_availability',
  'book_appointment',
  'request_handoff',
  'update_qualification',
  'record_opt_out',
  'send_approved_asset',
  'report_abuse',
  'schedule_commitment',
  // Setter §09.9: the prospect asked for (or kept using) another language on the call.
  'record_language',
  // Setter §09.4: structured objection on the opportunity (same tool as WhatsApp).
  'record_objection',
] as const satisfies readonly ('bootstrap_context' | (typeof AGENT_TOOLS)[number])[];
export const VoiceLiveActionSchema = z.enum(VOICE_LIVE_ACTIONS);
export type VoiceLiveAction = z.infer<typeof VoiceLiveActionSchema>;

/** How the server-issued session binding token reaches the live-action requests (PRD §11). */
export const BINDING_TRANSPORTS = ['context_variable', 'provider_call_id'] as const;
export const BindingTransportSchema = z.enum(BINDING_TRANSPORTS);
export type BindingTransport = z.infer<typeof BindingTransportSchema>;

export const BINDING_TRANSPORT_LABELS: Record<BindingTransport, string> = {
  context_variable: 'Token de sesión en una variable de contexto de la llamada',
  provider_call_id: 'Identificador de llamada de Aircall enviado por la acción',
};

/**
 * Which runtime talks on the call (PRD §8.2, §11):
 *  - aircall_native: Aircall's own AI agent (configured in Aircall; we only trigger calls and serve actions).
 *  - pipecat: our own voice pipeline (apps/voice-agent); Aircall numbers are forwarded into it.
 */
export const VOICE_RUNTIMES = ['aircall_native', 'pipecat'] as const;
export const VoiceRuntimeSchema = z.enum(VOICE_RUNTIMES);
export type VoiceRuntime = z.infer<typeof VoiceRuntimeSchema>;
export const VOICE_RUNTIME_LABELS: Record<VoiceRuntime, string> = {
  aircall_native: 'Agente de IA nativo de Aircall',
  pipecat: 'Servicio de voz propio (pipeline)',
};

const E164 = z.string().trim().regex(/^\+[1-9]\d{7,14}$/, 'Número en formato internacional (+52…)');

// ───────── Agent management gate ─────────

export const VoiceSettingEditabilitySchema = z.object({
  key: z.string(),
  label: z.string(),
  /** api = we can set it per call / through the public API; aircall_ui = only in Aircall's interface; read_only = we only read it. */
  editableVia: z.enum(['api', 'aircall_ui', 'read_only']),
  state: z.enum(['verified', 'unverified', 'unsupported', 'failed']),
  detail: z.string(),
});
export type VoiceSettingEditability = z.infer<typeof VoiceSettingEditabilitySchema>;

export const VoiceDeploymentSchema = z.object({
  id: Id,
  target: z.string(),
  status: DeploymentStatusSchema,
  detail: z.string().nullable(),
  promptVersion: z.number().int().nullable(),
  agentScope: z.enum(['workspace', 'project']).nullable(),
  globalVersion: z.number().int().nullable(),
  toolVersion: z.number().int().nullable(),
  appliedAt: Iso.nullable(),
  updatedAt: Iso,
});
export type VoiceDeployment = z.infer<typeof VoiceDeploymentSchema>;

export const VoiceAgentMappingSchema = z.object({
  id: Id,
  endpointId: Id,
  lineName: z.string(),
  linePhone: z.string().nullable(),
  lineShared: z.boolean(),
  /** null = the line's configured project (dedicated line) is used. */
  projectId: Id.nullable(),
  projectName: z.string().nullable(),
  aircallAgentId: z.string(),
  label: z.string().nullable(),
  target: z.string(),
  deployments: z.array(VoiceDeploymentSchema),
  /** Why this mapping cannot place calls yet (empty = no mapping-level blocker). */
  blockers: z.array(z.string()),
  updatedAt: Iso,
});
export type VoiceAgentMapping = z.infer<typeof VoiceAgentMappingSchema>;

export const VoiceConnectionSetupSchema = z.object({
  connectionId: Id,
  label: z.string(),
  status: z.string(),
  capabilities: CapabilityReportSchema,
  settingsMatrix: z.array(VoiceSettingEditabilitySchema),
  webhook: z.object({
    callbackUrl: z.string(),
    registered: z.boolean(),
    mode: z.enum(['api', 'manual']).nullable(),
    registeredAt: Iso.nullable(),
    lastEventAt: Iso.nullable(),
  }),
  actions: z.object({
    baseUrl: z.string(),
    secretConfigured: z.boolean(),
    secretRotatedAt: Iso.nullable(),
    bindingTransport: BindingTransportSchema,
  }),
  lines: z.array(
    z.object({
      endpointId: Id,
      displayName: z.string(),
      phoneE164: z.string().nullable(),
      shared: z.boolean(),
      projectIds: z.array(Id),
      defaultProjectId: Id.nullable(),
      /** Own voice service only: the telephony number this Aircall line forwards to (maps inbound calls to the line). */
      entryNumber: z.string().nullable(),
    }),
  ),
  /** Which runtime serves calls of this connection, and whether the own voice service is configured on the server. */
  runtime: z.object({ mode: VoiceRuntimeSchema, serviceConfigured: z.boolean() }),
  agents: z.array(VoiceAgentMappingSchema),
  outOfHoursGuidance: z.string(),
  /** CH-02 inbound calls (pilot, only if validated): setting + what must be verified before it can be turned on. */
  inbound: z.object({
    enabled: z.boolean(),
    /** enabled AND every required capability still verified (re-checked on every inbound event). */
    active: z.boolean(),
    canEnable: z.boolean(),
    requirements: z.array(z.object({ key: z.string(), label: z.string(), met: z.boolean(), required: z.boolean(), detail: z.string() })),
  }),
});
export type VoiceConnectionSetup = z.infer<typeof VoiceConnectionSetupSchema>;

export const VoiceSetupSchema = z.object({ items: z.array(VoiceConnectionSetupSchema) });

export const UpdateVoiceSettingsSchema = z.object({
  bindingTransport: BindingTransportSchema.optional(),
  runtime: VoiceRuntimeSchema.optional(),
  /** Own voice service: telephony entry number per line (null removes it). */
  entryNumbers: z.array(z.object({ endpointId: Id, entryNumber: E164.nullable() })).max(100).optional(),
  /** CH-02: only accepted when inbound_routing and session_binding are verified. */
  inboundEnabled: z.boolean().optional(),
  agents: z
    .array(
      z.object({
        endpointId: Id,
        projectId: Id.nullable().default(null),
        aircallAgentId: z.string().trim().min(1).max(64).regex(/^[A-Za-z0-9_-]+$/, 'Solo letras, números, guion y guion bajo'),
        label: z.string().trim().max(120).nullable().optional(),
      }),
    )
    .max(100)
    .optional(),
});

export const ActionSecretSchema = z.object({
  /** Shown exactly once. Configure it as the Authorization bearer of every live action in Aircall. */
  secret: z.string(),
  setup: VoiceConnectionSetupSchema,
});

export const RegisterAircallWebhookSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('api') }),
  z.object({ mode: z.literal('manual'), token: z.string().trim().min(8).max(200) }),
]);

export const ConfirmVoiceDeploymentSchema = z.object({ note: z.string().trim().min(3).max(500) });

export const SetVoiceCapabilitySchema = z.object({
  key: VoiceCapabilitySchema,
  state: z.enum(['verified', 'unverified', 'unsupported', 'failed']),
  /** Evidence of the real Phase 0 check (call id, date, who observed it). Required: never flip without evidence. */
  evidence: z.string().trim().min(10).max(1000),
});

export const TestVoiceActionAuthSchema = z.object({
  secret: z.string().trim().min(1).max(300),
  sessionToken: z.string().trim().max(300).optional(),
});
export const TestVoiceActionAuthResultSchema = z.object({
  secretValid: z.boolean(),
  binding: z.enum(['not_provided', 'valid', 'invalid', 'expired', 'other_connection']),
  detail: z.string(),
});

// ───────── Setup package (guided provider handoff) ─────────

export const VoiceActionSpecSchema = z.object({
  name: VoiceLiveActionSchema,
  description: z.string(),
  method: z.literal('POST'),
  url: z.string(),
  headers: z.array(z.object({ name: z.string(), value: z.string() })),
  inputSchema: z.record(z.string(), z.unknown()),
  timeoutMs: z.number().int(),
  sideEffect: z.boolean(),
});

export const VoiceSetupPackageSchema = z.object({
  generatedAt: Iso,
  connectionId: Id,
  aircallAgentId: z.string(),
  target: z.string(),
  line: z.object({ endpointId: Id, displayName: z.string(), phoneE164: z.string().nullable(), shared: z.boolean() }),
  project: z.object({ id: Id, name: z.string() }).nullable(),
  prompt: z.object({
    compiledText: z.string(),
    approxTokens: z.number().int(),
    globalVersion: z.number().int().nullable(),
    toolVersion: z.number().int().nullable(),
    workspacePromptVersion: z.number().int().nullable(),
    projectPromptVersion: z.number().int().nullable(),
    conflicts: z.array(z.object({ layer: z.string(), message: z.string() })),
  }),
  /** Placeholders our outbound trigger fills through `context` (greeting/voicemail only). */
  placeholders: z.array(z.object({ name: z.string(), description: z.string() })),
  actions: z.array(VoiceActionSpecSchema),
  bindingTransport: BindingTransportSchema,
  steps: z.array(z.string()),
  warnings: z.array(z.string()),
});
export type VoiceSetupPackage = z.infer<typeof VoiceSetupPackageSchema>;

// ───────── Calls ─────────

export const StartAiCallSchema = z.object({
  contactId: Id,
  projectId: Id,
  /** Aircall line; defaults to the only configured line/agent mapping that serves the project. */
  endpointId: Id.optional(),
  /** `test`: admin-only Phase 0 call to an allowlisted number while outbound calls are unverified. */
  mode: z.enum(['live', 'test']).default('live'),
  idempotencyKey: z.string().trim().min(8).max(120).regex(/^[\w.:-]+$/).optional(),
});

export const CALL_START_RESULTS = ['submitted', 'deferred', 'uncertain', 'failed', 'duplicate'] as const;
export const CallStartResultSchema = z.enum(CALL_START_RESULTS);

export const CallAttemptSchema = z.object({
  id: Id,
  attemptNumber: z.number().int(),
  providerKey: z.string(),
  status: CallStatusSchema,
  responseCode: z.number().int().nullable(),
  error: z.string().nullable(),
  submittedAt: Iso.nullable(),
  reconciledAt: Iso.nullable(),
  createdAt: Iso,
});
export type CallAttempt = z.infer<typeof CallAttemptSchema>;

export const CallTraceEntrySchema = z.object({ at: Iso, step: z.string(), detail: z.string().nullable() });

export const TranscriptSegmentSchema = z.object({
  speaker: z.enum(['agent', 'contact', 'unknown']),
  text: z.string(),
  startSeconds: z.number().nullable(),
});

export const VOICEMAIL_TRANSCRIPT_STATUSES = ['not_configured', 'pending', 'ready', 'failed'] as const;
export const VoicemailTranscriptStatusSchema = z.enum(VOICEMAIL_TRANSCRIPT_STATUSES);
export type VoicemailTranscriptStatus = z.infer<typeof VoicemailTranscriptStatusSchema>;
export const VOICEMAIL_TRANSCRIPT_STATUS_LABELS: Record<VoicemailTranscriptStatus, string> = {
  not_configured: 'Transcripción no configurada',
  pending: 'Transcribiendo',
  ready: 'Transcripción lista',
  failed: 'No se pudo transcribir',
};
export const VoicemailDetailSchema = z.object({
  /** null while the message is not stored yet. */
  transcriptStatus: VoicemailTranscriptStatusSchema.nullable(),
  transcript: z.string().nullable(),
  detail: z.string(),
});
export type VoicemailDetail = z.infer<typeof VoicemailDetailSchema>;

export const CallDetailSchema = z.object({
  id: Id,
  contactId: Id,
  contactName: z.string().nullable(),
  contactPhone: z.string().nullable(),
  projectId: Id.nullable(),
  projectName: z.string().nullable(),
  direction: DirectionSchema,
  status: CallStatusSchema,
  outcome: z.string().nullable(),
  origin: z.enum(['manual', 'routine', 'test', 'inbound', 'unknown']),
  line: z.object({ endpointId: Id, displayName: z.string(), phoneE164: z.string().nullable() }).nullable(),
  aircallAgentId: z.string().nullable(),
  providerRequestId: z.string().nullable(),
  providerCallId: z.string().nullable(),
  queueDeadlineAt: Iso.nullable(),
  deferredUntil: Iso.nullable(),
  startedAt: Iso.nullable(),
  endedAt: Iso.nullable(),
  durationSeconds: z.number().int().nullable(),
  attempts: z.array(CallAttemptSchema),
  trace: z.array(CallTraceEntrySchema),
  analysis: z.object({
    status: AnalysisStatusSchema,
    summary: z.string().nullable(),
    transcript: z.array(TranscriptSegmentSchema).nullable(),
    /** Answers to the agent's intake questions (Aircall AI agent summary), shown as delivered by the provider. */
    intake: z.array(z.object({ question: z.string(), answer: z.string() })).nullable(),
    detail: z.string().nullable(),
  }),
  recording: z.object({
    capability: z.enum(['verified', 'unverified', 'unsupported', 'failed']),
    available: z.boolean(),
    canAccess: z.boolean(),
    detail: z.string(),
    /** Plan B4: what happened with the recording (Telnyx calls carry a recording decision; Aircall calls are `provider`). */
    state: CallRecordingStateSchema.default('none'),
    /** Consent notice of a Telnyx call (null when no notice applied). */
    consent: CallConsentSchema.nullable().default(null),
    durationSeconds: z.number().int().nullable().default(null),
  }),
  /** Plan B7: the caller left a voicemail (the recording above is the message); transcription only when configured. */
  voicemail: VoicemailDetailSchema.nullable().default(null),
  promptVersionRef: z
    .object({
      target: z.string().nullable(),
      appliedPromptVersion: z.number().int().nullable(),
      appliedAt: Iso.nullable(),
      pendingConfirmation: z.boolean(),
    })
    .nullable(),
  liveActions: z.array(z.object({ at: Iso, action: z.string(), ok: z.boolean(), ms: z.number().int(), error: z.string().nullable() })),
  /** Own voice service only: what the session reported. null for Aircall-native calls. */
  session: z
    .object({
      transport: z.enum(['webrtc', 'telnyx']).nullable(),
      /** true = ran with scripted STT/LLM/TTS doubles: a simulation, never evidence of a real call. */
      simulated: z.boolean(),
      /** Browser test session: tools ran in a sandbox (no real bookings, handoffs or lead changes). */
      sandbox: z.boolean(),
      model: z.object({ provider: z.string(), model: z.string() }).nullable(),
      turns: z.number().int(),
      interruptions: z.number().int(),
      /** End of the person's speech → first agent audio, per turn (as measured by the voice service). */
      latency: z.object({ samples: z.number().int(), p50Ms: z.number().int().nullable(), p95Ms: z.number().int().nullable(), maxMs: z.number().int().nullable() }),
      errors: z.array(z.object({ at: Iso, code: z.string(), detail: z.string().nullable() })),
    })
    .nullable()
    .default(null),
  reviewTaskId: Id.nullable(),
  /** Setter §06.10/§09.12: controls the voice service applied, end reason, duration and estimated cost. */
  controls: CallControlsSummarySchema.nullable().default(null),
  /** Setter §05.22: live transfer to an advisor asked on this call and whether the advisor answered (sin verificar). */
  liveTransfer: LiveTransferViewSchema.nullable().default(null),
  /** Inbound calls (CH-02): how the caller/project were resolved, hours, callback continuity. null for outbound. */
  inbound: z
    .object({
      aiHandled: z.boolean(),
      outOfHours: z.boolean(),
      guidance: z.string().nullable(),
      contactMatch: z.enum(['existing', 'created', 'ambiguous_created']),
      projectResolution: z.enum(['line', 'active_interest', 'ask', 'none']),
      linkedTaskIds: z.array(Id),
      returnedCallIds: z.array(Id),
      callbackTaskId: Id.nullable(),
    })
    .nullable()
    .default(null),
  createdAt: Iso,
});
export type CallDetail = z.infer<typeof CallDetailSchema>;

export const StartAiCallResultSchema = z.object({
  result: CallStartResultSchema,
  reason: z.string().nullable(),
  call: CallDetailSchema,
});
export type StartAiCallResult = z.infer<typeof StartAiCallResultSchema>;

export const CallRecordingSchema = z.object({
  available: z.boolean(),
  /** Aircall: temporary provider link. Telnyx (plan B4): the authenticated LIA route that streams the stored file. */
  url: z.string().nullable(),
  expiresAt: Iso.nullable(),
  detail: z.string(),
  source: z.enum(['aircall', 'lia']).nullable().default(null),
});

// ───────── Readiness & latency ─────────

export const VoiceReadinessSchema = z.object({
  /** "Llamar ahora" is enabled only when this is true. */
  canCall: z.boolean(),
  /** Admin Phase 0 test call to allowlisted numbers. */
  canTestCall: z.boolean(),
  reasons: z.array(z.string()),
  lines: z.array(
    z.object({
      endpointId: Id,
      connectionId: Id,
      displayName: z.string(),
      projectIds: z.array(Id),
      agentConfigured: z.boolean(),
      shared: z.boolean(),
    }),
  ),
});
export type VoiceReadiness = z.infer<typeof VoiceReadinessSchema>;

export const VoiceLatencyQuerySchema = z.object({ days: z.coerce.number().int().min(1).max(90).default(7) });
export const VoiceLatencyStatsSchema = z.object({
  since: Iso,
  sampleCalls: z.number().int(),
  items: z.array(
    z.object({
      action: z.string(),
      count: z.number().int(),
      p50Ms: z.number().int().nullable(),
      p95Ms: z.number().int().nullable(),
      maxMs: z.number().int().nullable(),
      errorRate: z.number().nullable(),
      timeouts: z.number().int(),
    }),
  ),
  note: z.string(),
  /**
   * Own voice service: end of the person's speech → first agent audio, per turn, across real sessions (browser
   * tests and simulated sessions are excluded). This is the number to compare with the prototype's baseline.
   */
  turns: z
    .object({ sampleCalls: z.number().int(), samples: z.number().int(), p50Ms: z.number().int().nullable(), p95Ms: z.number().int().nullable(), maxMs: z.number().int().nullable(), note: z.string() })
    .default({ sampleCalls: 0, samples: 0, p50Ms: null, p95Ms: null, maxMs: null, note: '' }),
});
export type VoiceLatencyStats = z.infer<typeof VoiceLatencyStatsSchema>;

// ───────── Machine routes ─────────

// ───────── Own voice service (apps/voice-agent) — see docs/voice-service.md ─────────

export const VoiceServiceStatusSchema = z.object({
  configured: z.boolean(),
  /** null = not checked (service not configured). */
  reachable: z.boolean().nullable(),
  detail: z.string(),
  version: z.string().nullable(),
  testDoubles: z.boolean().nullable(),
  telephonyConfigured: z.boolean().nullable(),
  llmProviders: z.array(z.string()),
  /** The model the platform assigns to voice right now, and whether the voice service holds a key for it. */
  model: z.object({ state: z.enum(['enabled', 'disabled', 'simulated']), provider: z.string().nullable(), model: z.string().nullable(), reason: z.string().nullable(), keyAvailable: z.boolean().nullable() }),
  checkedAt: Iso,
});
export type VoiceServiceStatus = z.infer<typeof VoiceServiceStatusSchema>;

export const CreateVoiceWebSessionSchema = z.object({ projectId: Id });
export const VoiceWebSessionSchema = z.object({
  callId: Id,
  /** Bound to this one test call; useless without the voice service's own secret. */
  sessionToken: z.string(),
  /** Browser-reachable WebRTC offer endpoint of the voice service. */
  offerUrl: z.string(),
  expiresAt: Iso,
});
export type VoiceWebSession = z.infer<typeof VoiceWebSessionSchema>;

export const VOICE_SESSION_EVENT_TYPES = ['session.started', 'call.ringing', 'call.answered', 'call.ended', 'turn.user', 'turn.assistant', 'turn.latency', 'usage', 'recording', 'error', 'language.changed'] as const;
const Ms = z.number().int().min(0).max(600_000);
const EvBase = { id: z.string().min(6).max(80), at: Iso };
export const VoiceSessionEventSchema = z.discriminatedUnion('type', [
  z.object({ ...EvBase, type: z.literal('session.started'), transport: z.enum(['webrtc', 'telnyx']), providerCallId: z.string().max(200).nullable().optional(), testDoubles: z.boolean().default(false) }),
  z.object({ ...EvBase, type: z.literal('call.ringing') }),
  z.object({ ...EvBase, type: z.literal('call.answered') }),
  z.object({
    ...EvBase,
    type: z.literal('call.ended'),
    reason: z.enum(['completed', 'no_answer', 'busy', 'voicemail', 'failed']),
    durationSeconds: z.number().min(0).max(86_400).nullable().optional(),
    detail: z.string().max(300).nullable().optional(),
    /** Setter §06.10/§09.12: why the voice service closed the call (inactivity, duration, audio problem, abuse flag). */
    endReason: CallEndReasonSchema.nullable().optional(),
  }),
  /** A call control the voice service applied (silence check, steer, extension/close, clarification…). */
  z.object({ ...EvBase, type: z.literal('call.control'), control: CallControlKindSchema, elapsedSeconds: z.number().min(0).max(86_400), detail: z.string().max(300).nullable().optional() }),
  z.object({ ...EvBase, type: z.literal('turn.user'), index: z.number().int().min(0).max(2000), text: z.string().max(4000) }),
  z.object({ ...EvBase, type: z.literal('turn.assistant'), index: z.number().int().min(0).max(2000), text: z.string().max(4000), interrupted: z.boolean().default(false) }),
  z.object({ ...EvBase, type: z.literal('turn.latency'), index: z.number().int().min(0).max(2000), totalMs: Ms, sttMs: Ms.nullable().optional(), llmTtfbMs: Ms.nullable().optional(), toolsMs: Ms.nullable().optional(), ttsTtfbMs: Ms.nullable().optional() }),
  z.object({
    ...EvBase,
    type: z.literal('usage'),
    llm: z.object({ provider: z.string().max(40), model: z.string().max(120), inputTokens: z.number().int().min(0), outputTokens: z.number().int().min(0), cachedTokens: z.number().int().min(0).optional() }).nullable().optional(),
    ttsCharacters: z.number().int().min(0).nullable().optional(),
    sttSeconds: z.number().min(0).nullable().optional(),
  }),
  z.object({ ...EvBase, type: z.literal('recording'), ref: z.string().min(1).max(500) }),
  z.object({ ...EvBase, type: z.literal('error'), code: z.string().max(60), detail: z.string().max(300).nullable().optional() }),
  // Setter §09.9: the session switched language (stored as the contact's preferred language; CRM via mapping).
  z.object({ ...EvBase, type: z.literal('language.changed'), ...VoiceLanguageChangedFields }),
]);
export type VoiceSessionEvent = z.infer<typeof VoiceSessionEventSchema>;
export const VoiceSessionEventsSchema = z.object({ events: z.array(VoiceSessionEventSchema).min(1).max(50) });
export const VoiceSessionEventsAckSchema = z.object({ accepted: z.number().int(), duplicates: z.number().int() });

export const VoiceSessionToolSchema = z.object({
  name: z.string(),
  description: z.string(),
  parameters: z.record(z.string(), z.unknown()),
  sideEffect: z.boolean(),
  timeoutMs: z.number().int(),
  ackPhrase: z.string().nullable(),
  /** Latency hint: true = the voice service speaks a short filler while the action runs. */
  slow: z.boolean(),
});
export const VoiceSessionBootstrapSchema = z.object({
  callId: Id,
  direction: DirectionSchema,
  mode: z.enum(['live', 'test']),
  language: z.string(),
  agentName: z.string().nullable(),
  instructions: z.string(),
  opening: z.object({ contactFirstName: z.string().nullable(), projectName: z.string().nullable(), projectCandidates: z.array(z.string()) }),
  tools: z.array(VoiceSessionToolSchema),
  model: z.object({ provider: z.enum(['openai', 'anthropic', 'openai_compatible']), model: z.string(), baseUrl: z.string().nullable(), temperature: z.number().nullable(), maxOutputTokens: z.number().int().nullable() }).nullable(),
  /** Setter §06.10.1/§06.10.2/§09.12: per-project call controls (cap clamped to the platform VOICE_MAX_CALL_SECONDS). */
  limits: z.object({
    maxDurationSeconds: z.number().int(),
    silencePromptSeconds: z.number().int(),
    silenceHangupSeconds: z.number().int(),
    steerAtSeconds: z.number().int(),
    softLimitSeconds: z.number().int(),
    maxClarificationAttempts: z.number().int(),
  }),
  /** §09.12: may the closing offer to continue on WhatsApp (connected + permission + not suppressed/blocked)? */
  followUp: z.object({ whatsappAllowed: z.boolean() }),
  /** Setter §09.9–§09.11: per-project voice, speaking rate, bilingual switching and barge-in thresholds («sin verificar»). */
  voice: VoiceSessionVoiceSchema,
  versions: z.object({ global: z.number().int().nullable(), tools: z.number().int().nullable(), workspace: z.number().int().nullable(), project: z.number().int().nullable() }),
  expiresAt: Iso,
});
export type VoiceSessionBootstrap = z.infer<typeof VoiceSessionBootstrapSchema>;

export const VoiceInboundRequestSchema = z.object({ transport: z.enum(['telnyx']), providerCallId: z.string().min(1).max(200), to: z.string().min(3).max(32), from: z.string().max(32).nullable().optional() });
export const VoiceInboundResponseSchema = z.discriminatedUnion('accept', [
  z.object({ accept: z.literal(true), sessionToken: z.string(), callId: Id }),
  z.object({ accept: z.literal(false), reason: z.enum(['out_of_hours', 'unknown_number', 'inbound_disabled', 'not_ready']), message: z.string() }),
]);

/** Telnyx media stream of a call LIA dialed/answered (plan B2): the voice service redeems the one-time ticket. */
export const VoiceStreamRequestSchema = z.object({ ticket: z.string().min(20).max(400), providerCallId: z.string().min(1).max(200) });
export const VoiceStreamResponseSchema = z.discriminatedUnion('accept', [
  z.object({ accept: z.literal(true), sessionToken: z.string(), callId: Id }),
  z.object({ accept: z.literal(false), reason: z.enum(['invalid_ticket', 'ticket_used', 'call_mismatch', 'call_ended']), message: z.string() }),
]);
/** The voice service asks LIA to hang up the Telnyx leg of a call LIA controls (the service has no key for it). */
export const VoiceHangupResponseSchema = z.object({ ok: z.boolean(), detail: z.string() });

/**
 * Copilot transcription of a human Telnyx call (plan B5). The voice service received a Telnyx media stream whose URL
 * carries a copilot ticket (`lct_…`): it redeems it ONCE, with the Telnyx call_control_id of that stream, for a stream
 * token, then posts final utterances (transcription only — it never speaks on the call and never runs an LLM).
 */
export const CopilotStreamRedeemSchema = z.object({ ticket: z.string().min(20).max(400), providerCallId: z.string().min(1).max(200) });
export const COPILOT_STREAM_REFUSALS = ['invalid_ticket', 'ticket_used', 'call_mismatch', 'call_ended', 'module_disabled'] as const;
export const CopilotStreamRedeemResponseSchema = z.discriminatedUnion('accept', [
  z.object({
    accept: z.literal(true),
    streamToken: z.string(),
    sessionId: z.string(),
    /** Party on Telnyx's `inbound` track of the streamed leg; the `outbound` track is the other one. */
    inboundSpeaker: z.enum(['external', 'internal']),
    language: z.string(),
  }),
  z.object({ accept: z.literal(false), reason: z.enum(COPILOT_STREAM_REFUSALS), message: z.string() }),
]);
export const CopilotStreamTrackSchema = z.enum(['inbound', 'outbound']);
export const CopilotStreamUtteranceSchema = z.object({
  /** Stable id within the stream (`<track>-<n>`): a retried batch is deduplicated by it. */
  id: z.string().min(1).max(100).regex(/^[A-Za-z0-9_.:-]+$/),
  track: CopilotStreamTrackSchema,
  text: z.string().min(1).max(4000),
  /** Offsets from the start of the stream (milliseconds). */
  startMs: z.number().int().min(0).nullable(),
  endMs: z.number().int().min(0).nullable(),
});
export const CopilotStreamUtterancesSchema = z.object({
  utterances: z.array(CopilotStreamUtteranceSchema).max(200),
  /** The media stream ended (last batch). Informational: the call's end comes from Telnyx's events. */
  final: z.boolean().optional(),
});
export const CopilotStreamUtterancesAckSchema = z.object({ status: z.enum(['applied', 'skipped', 'quarantined']), detail: z.string() });

export const AircallWebhookAckSchema = z.object({ received: z.boolean(), duplicate: z.boolean() });
export const VoiceLiveActionResponseSchema = z.record(z.string(), z.unknown());
